import type { DestProtocol, Profile, ProfileProtocol } from '../api'
import { currentLang, t } from '../i18n'

/**
 * Protocol to the platform (docs/youtube-hls.md §4): RTMPS (the default, first in
 * Profile.protocols) or HLS for YouTube. Shown in Advanced next to the hold, like the
 * Studio S1 handoff. Hints come from the profile (EN); ES and RU from i18n.ts by
 * protocol id. The backup copy (copy=1) is not offered here: it doubles the traffic
 * and is the owner's decision (HLS-2).
 */

/** The profile's protocols when there is a choice (two or more); else none. */
export function choiceOf(p?: Profile): ProfileProtocol[] {
  const list = p?.protocols ?? []
  return list.length > 1 ? list : []
}

/** What the profile says about this protocol, in the cabinet's language. */
export function protocolHints(pp?: ProfileProtocol): string[] {
  if (!pp) return []
  if (pp.id === 'hls' && currentLang() !== 'en') return [t('proto.hls.hint1'), t('proto.hls.hint2'), t('proto.hls.hint3')]
  return pp.hints ?? []
}

/** The server address must fit the protocol: https:// for HLS, rtmp(s):// otherwise. */
export function urlErrorFor(url: string, protocol: DestProtocol): string | undefined {
  const u = url.trim()
  if (!u) return undefined
  if (protocol === 'hls') return /^https:\/\//i.test(u) ? undefined : t('proto.urlHttps')
  return /^rtmps?:\/\//i.test(u) ? undefined : t('add.urlError')
}

/** The protocol's name as the profile gives it (RTMPS, HLS). */
export function protocolName(protocols: ProfileProtocol[], id: DestProtocol): string {
  return protocols.find((x) => x.id === id)?.name ?? id.toUpperCase()
}

export function ProtocolSettings({ testId, protocols, value, saved, platformLabel, name, live, keySteps, disabled, onChange }: {
  testId: string;
  /** two or more, the default first */
  protocols: ProfileProtocol[];
  value: DestProtocol;
  /** the saved protocol of an existing destination; absent for a new one */
  saved?: DestProtocol;
  platformLabel: string;
  /** the destination's name for the notes */
  name: string;
  /** the destination is on air: a switch reconnects it */
  live: boolean;
  /** show the protocol's key steps under the hints (the editor; the add form shows them on top) */
  keySteps?: boolean;
  disabled?: boolean;
  onChange: (v: DestProtocol) => void;
}) {
  const pp = protocols.find((x) => x.id === value)
  const hints = protocolHints(pp)
  const changed = saved !== undefined && value !== saved
  const steps = keySteps && changed && value !== protocols[0]!.id ? pp?.key_steps ?? [] : []
  return (
    <div data-testid={testId} data-protocol={value} className="flex flex-col gap-2.5 rounded-md border border-line p-3">
      <span id={`${testId}-label`} className="text-[13px] font-medium text-text">{t('proto.title', { platform: platformLabel })}</span>
      <div role="radiogroup" aria-labelledby={`${testId}-label`} className="grid grid-cols-2 gap-1.5">
        {protocols.map((x, i) => {
          const sel = x.id === value
          return (
            <button key={x.id} type="button" role="radio" aria-checked={sel} data-testid={`${testId}-${x.id}`} disabled={disabled}
              onClick={() => onChange(x.id)}
              className={`flex min-h-[44px] flex-col items-start gap-0.5 rounded-md border px-2.5 py-2 text-left ${sel ? 'border-live/50 bg-live/[.12]' : 'border-line-2 hover:border-muted'}`}>
              {/* the selection is the border, the fill and the ✓, not the colour alone */}
              <span className={`font-mono text-[13px] font-medium ${sel ? 'text-live' : 'text-text'}`}>{sel ? '✓ ' : ''}{x.name}</span>
              <span className="text-[11px] text-muted">
                {i === 0 && <span data-testid={`${testId}-recommended`} className="font-medium text-text-soft">{t('proto.recommended')}. </span>}
                {t(x.id === 'hls' ? 'proto.hls.sub' : 'proto.rtmps.sub')}
              </span>
            </button>
          )
        })}
      </div>
      {value !== protocols[0]!.id && hints.length > 0 && (
        <ul data-testid={`${testId}-hints`} className="m-0 flex list-none flex-col gap-1 p-0 text-[11px] text-text-soft">
          {hints.map((h, i) => <li key={i} className="flex gap-1.5"><span aria-hidden className="text-muted">·</span><span>{h}</span></li>)}
        </ul>
      )}
      {steps.length > 0 && (
        <div data-testid={`${testId}-steps`} className="flex flex-col gap-1.5 border-t border-line pt-2">
          <span className="text-xs font-medium text-text-soft">{t('proto.keySteps', { proto: pp?.name ?? '' })}</span>
          <ol className="m-0 flex list-none flex-col gap-1.5 p-0 text-xs text-text-soft">
            {steps.map((s, i) => <li key={i} className="flex gap-2"><span className="font-mono font-medium text-accent">{i + 1}</span><span>{s}</span></li>)}
          </ol>
        </div>
      )}
      {changed && (
        <p data-testid={`${testId}-note`} className="m-0 text-[11px] text-muted">{live ? t('proto.liveNote', { name }) : t('proto.offNote', { name })}</p>
      )}
    </div>
  )
}

/** "HLS" next to an output's name (card, Status): the text says it, not the colour. RTMPS has no chip. */
export function ProtocolChip({ protocol, platformLabel, testId }: { protocol?: DestProtocol; platformLabel: string; testId?: string }) {
  if (protocol !== 'hls') return null
  return (
    <span data-testid={testId} title={t('proto.chip', { platform: platformLabel })} aria-label={t('proto.chip', { platform: platformLabel })}
      className="flex-none rounded-md border border-line-2 px-[5px] py-px font-mono text-[10px] font-medium tracking-[.06em] text-text-soft">HLS</span>
  )
}
